import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Input, Select, Button, Alert, useIsMobile } from '../components/ui';
import api from '../utils/api';

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const STEPS = ['Basic Info', 'Physical Details', 'Done'];
const OPTIONAL_COMPONENTS = [
  { value: 'platelets', label: 'Platelets', desc: 'Apheresis appointment, ~1-2 hours, can give every 7 days' },
  { value: 'plasma', label: 'Plasma', desc: 'Apheresis appointment, ~1 hour, can give every 28 days' },
];

export default function DonorSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ blood_type: '', date_of_birth: '', gender: '', weight_kg: '', extra_components: [] });

  const set = k => e => { setError(''); setForm(f => ({ ...f, [k]: e.target.value })); };

  const validateStep0 = () => {
    if (!form.blood_type) return 'Please select your blood type.';
    if (!form.date_of_birth) return 'Please enter your date of birth.';
    if (!form.gender) return 'Please select your gender.';
    const age = Math.floor((Date.now() - new Date(form.date_of_birth)) / (365.25 * 24 * 3600 * 1000));
    if (age < 18) return 'You must be at least 18 years old to register as a donor.';
    if (age > 65) return 'Donors must be 65 years old or younger.';
    return null;
  };

  const validateStep1 = () => {
    const w = parseFloat(form.weight_kg);
    if (!form.weight_kg || isNaN(w)) return 'Please enter your weight.';
    if (w < 30 || w > 300) return 'Weight must be between 30 and 300 kg.';
    return null;
  };

  const handleNext = () => {
    const err = validateStep0();
    if (err) { setError(err); return; }
    setStep(1);
  };

  const handleSubmit = async () => {
    const err = validateStep1();
    if (err) { setError(err); return; }
    setLoading(true); setError('');
    try {
      await api.post('/donors/profile', {
        blood_type: form.blood_type,
        date_of_birth: form.date_of_birth,
        gender: form.gender,
        weight_kg: parseFloat(form.weight_kg),
        donatable_components: ['whole_blood', ...form.extra_components],
      });
      setStep(2);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 400 && detail?.includes('already exists')) { navigate(redirect || '/donor/profile'); return; }
      setError(detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: 'calc(100vh - 60px)',
      background: 'var(--gray-50)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: isMobile ? '24px 16px' : '48px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: '480px', animation: 'bb-fadeUp 0.5s ease both' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '32px', marginBottom: '10px' }}>🩸</div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '6px', color: 'var(--gray-900)' }}>
            Complete your donor profile
          </h1>
          <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', lineHeight: 1.6 }}>
            Takes 1 minute · Required before you can donate
          </p>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0', marginBottom: '24px' }}>
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: i < step ? 'var(--green)' : i === step ? 'var(--red)' : 'var(--gray-200)',
                  color: i <= step ? 'white' : 'var(--gray-500)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700, flexShrink: 0,
                  transition: 'background 0.25s',
                }}>
                  {i < step ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: '10px', fontWeight: 600, color: i === step ? 'var(--red)' : 'var(--gray-400)', whiteSpace: 'nowrap' }}>
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div style={{
                  flex: 1, height: '2px', marginBottom: '14px',
                  background: i < step ? 'var(--green)' : 'var(--gray-200)',
                  transition: 'background 0.25s',
                }} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 0 */}
        {step === 0 && (
          <Card>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '18px' }}>Basic Information</div>

            <Select label="Blood Type *" value={form.blood_type} onChange={set('blood_type')}>
              <option value="">Select your blood type</option>
              {BLOOD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
            <Input
              label="Date of Birth *"
              type="date"
              value={form.date_of_birth}
              onChange={set('date_of_birth')}
              max={new Date(Date.now() - 18 * 365.25 * 24 * 3600 * 1000).toISOString().split('T')[0]}
            />
            <Select label="Gender *" value={form.gender} onChange={set('gender')}>
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other / Prefer not to say</option>
            </Select>

            {error && <Alert variant="error">{error}</Alert>}
            <Button variant="primary" fullWidth onClick={handleNext}>Continue →</Button>
          </Card>
        )}

        {/* Step 1 */}
        {step === 1 && (
          <Card>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '4px' }}>Physical Details</div>
            <p style={{ fontSize: '13px', color: 'var(--gray-500)', marginBottom: '18px', lineHeight: 1.5 }}>
              Used to verify donation eligibility (minimum 50 kg required).
            </p>

            <Input
              label="Weight (kg) *"
              type="number"
              placeholder="e.g. 68"
              min="30" max="300" step="0.1"
              value={form.weight_kg}
              onChange={set('weight_kg')}
            />

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '4px' }}>
                Also willing to give? (optional)
              </div>
              <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginBottom: '10px', lineHeight: 1.5 }}>
                Whole blood is included by default. These need a scheduled apheresis appointment, not a walk-in.
              </p>
              {OPTIONAL_COMPONENTS.map(c => {
                const checked = form.extra_components.includes(c.value);
                return (
                  <label
                    key={c.value}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: '10px',
                      padding: '10px 12px', borderRadius: '9px', cursor: 'pointer',
                      border: `1.5px solid ${checked ? 'var(--red)' : 'var(--gray-200)'}`,
                      background: checked ? 'var(--red-light)' : 'var(--white)',
                      marginBottom: '8px', transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => setForm(f => ({
                        ...f,
                        extra_components: checked
                          ? f.extra_components.filter(v => v !== c.value)
                          : [...f.extra_components, c.value],
                      }))}
                      style={{ marginTop: '2px' }}
                    />
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--gray-900)' }}>{c.label}</div>
                      <div style={{ fontSize: '11.5px', color: 'var(--gray-500)' }}>{c.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Summary */}
            <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: '9px', padding: '13px 15px', marginBottom: '18px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Summary</div>
              {[['Blood type', form.blood_type], ['Date of birth', form.date_of_birth], ['Gender', form.gender]].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '3px 0' }}>
                  <span style={{ color: 'var(--gray-500)' }}>{k}</span>
                  <span style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{v}</span>
                </div>
              ))}
            </div>

            {error && <Alert variant="error">{error}</Alert>}

            <div style={{ display: 'flex', gap: '10px' }}>
              <Button variant="secondary" onClick={() => { setError(''); setStep(0); }} style={{ flexShrink: 0 }}>← Back</Button>
              <Button variant="primary" fullWidth loading={loading} onClick={handleSubmit}>Save Profile</Button>
            </div>
          </Card>
        )}

        {/* Step 2: Done */}
        {step === 2 && (
          <Card>
            <div style={{ textAlign: 'center', padding: '20px 0 12px' }}>
              <div style={{ fontSize: '52px', marginBottom: '14px' }}>✅</div>
              <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '8px', color: 'var(--gray-900)', letterSpacing: '-0.02em' }}>
                Profile created!
              </div>
              <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginBottom: '26px', lineHeight: 1.65 }}>
                {redirect
                  ? "You're registered as a donor. Head back to the request to respond."
                  : "You're registered as a donor. Complete the health screening next to confirm your eligibility."}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {redirect ? (
                  <>
                    <Button variant="primary" fullWidth onClick={() => navigate(redirect)}>
                      Continue to request →
                    </Button>
                    <Button variant="ghost" fullWidth onClick={() => navigate('/donor/profile?tab=screening')}>
                      Complete health screening first
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="primary" fullWidth onClick={() => navigate('/donor/profile?tab=screening')}>
                      Complete Health Screening →
                    </Button>
                    <Button variant="ghost" fullWidth onClick={() => navigate('/donor/profile')}>
                      View my profile
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}
